import { Box, Center, Checkbox, Spinner, Text, VStack } from "@chakra-ui/react";
import { useMemo, useState } from "react";

import { CodeNameRecord } from "api";
import { useRequestParams } from "contexts/RequestParamsProvider";
import { DocumentsFilter, FilterActionButtons } from "../DocumentsFilter";
import { GenericFilterProps } from "./generic-filter-props";

type ProductFilterProps = {
  productOptions: CodeNameRecord[] | null | undefined;
  isLoading?: boolean;
} & GenericFilterProps;

const ProductPickerAndActionButtons = ({
  filterKey,
  productOptions,
  productFilter,
  isLoading,
}: {
  filterKey: string;
  productOptions: CodeNameRecord[] | null | undefined;
  productFilter?: string[];
  isLoading?: boolean;
}) => {
  const { onChangeSelect } = useRequestParams();
  const [selectedProducts, setSelectedProducts] = useState<{
    [key: string]: boolean;
  }>(() =>
    // map the saved productFilter to our state object that contains [product]: boolean
    (productFilter ?? []).reduce(
      (obj, product) => ({ ...obj, [product]: true }),
      {}
    )
  );

  // map the product object to an array of selected products that the filter context will use
  const selectedFilterState = useMemo(
    () =>
      Object.entries(selectedProducts)
        .filter(([, value]) => value)
        .map(([product]) => product)
        .sort((a, b) => a.localeCompare(b)),
    [selectedProducts]
  );

  // group products by category
  const groupedProducts = useMemo(
    () =>
      productOptions?.reduce(
        (groups: Record<string, CodeNameRecord[]>, product) => {
          const category = product.Category;
          if (category) {
            if (!groups[category]) {
              groups[category] = [];
            }
            groups[category].push(product);
          }
          return groups;
        },
        {}
      ),
    [productOptions]
  );

  const onSet = () => {
    onChangeSelect(filterKey, selectedFilterState);
  };

  const onClear = () => {
    setSelectedProducts({});
    onChangeSelect(filterKey, undefined);
  };

  return (
    <>
      {isLoading ? (
        <Center flex={1} minH="200px">
          <Spinner />
        </Center>
      ) : (
        <VStack
          mb={6}
          gap={0}
          alignItems="flex-start"
          overflowY="auto"
          maxH="440px"
        >
          {groupedProducts &&
            Object.entries(groupedProducts).map(([category, products]) => (
              <VStack key={category} w="full" spacing={0}>
                <Box
                  as="span"
                  bgColor="grey.50"
                  fontWeight="black"
                  textAlign="start"
                  textStyle="label"
                  px={6}
                  py={2}
                  w="full"
                >
                  {category?.toUpperCase()}
                </Box>
                {products.map((product) => {
                  const productCode = product.Code;

                  return productCode ? (
                    <Checkbox
                      key={product.Code}
                      isChecked={selectedProducts[productCode] ?? false}
                      onChange={(e) =>
                        setSelectedProducts((current) => ({
                          ...current,
                          [productCode]: e.target.checked,
                        }))
                      }
                      px={6}
                      py={3}
                      w="full"
                      _hover={{ bg: "navy.50" }}
                    >
                      <Text fontSize="sm">{product.AltName}</Text>
                    </Checkbox>
                  ) : null;
                })}
              </VStack>
            ))}
        </VStack>
      )}

      <FilterActionButtons
        onReset={onClear}
        isResetDisabled={
          selectedFilterState.length === 0 && (productFilter ?? []).length === 0
        }
        onSet={onSet}
        isSetDisabled={
          selectedFilterState.length === (productFilter?.length ?? 0) &&
          selectedFilterState.every((v, i) => v === productFilter?.[i])
        }
      />
    </>
  );
};

export const ProductFilter = ({
  filterKey,
  variant,
  name,
  productOptions,
  isLoading,
}: ProductFilterProps) => {
  const { filters } = useRequestParams();
  const productFilter = filters[filterKey] as string[] | undefined;

  const isSet = !!productFilter && productFilter.length > 0;

  return (
    <DocumentsFilter
      name={name}
      buttonName={isSet ? `${name} (${productFilter.length})` : name}
      isSet={isSet}
      variant={variant}
    >
      <ProductPickerAndActionButtons
        {...{
          productFilter,
          productOptions,
          filterKey,
          isLoading,
        }}
      />
    </DocumentsFilter>
  );
};
